
import React from "react";

const fanModels = [
  {
    name: "Residential Standard",
    image:
      "https://public.readdy.ai/ai/img_res/629624afa3760436f964e4a2660ca1cc.jpg",
    cfm: "1,200 CFM",
    coverage: "Up to 1,500 sq ft",
    panel: "15W Solar Panel",
    price: "$595",
    badge: "",
  },
  {
    name: "High-Performance Pro",
    image:
      "https://readdy.ai/api/search-image?query=Professional%20product%20photograph%20of%20a%20large%20high-performance%20solar-powered%20attic%20ventilation%20fan%20unit%20on%20a%20clean%20white%20background%2C%20larger%20integrated%20solar%20panel%20on%20top%2C%20premium%20black%20and%20silver%20housing%20professional%20studio%20lighting%20with%20soft%20shadows%2C%20commercial%20product%20photography%20with%20sharp%20detail%20and%20clean%20composition&width=400&height=400&seq=fan-pro-002&orientation=squarish",
    cfm: "1,800 CFM",
    coverage: "Up to 2,200 sq ft",
    panel: "25W Solar Panel",
    price: "$795",
    badge: "BEST SELLER",
  },
  {
    name: "Commercial Grade",
    image:
      "https://readdy.ai/api/search-image?query=Professional%20product%20photograph%20of%20a%20heavy-duty%20commercial%20grade%20solar-powered%20attic%20ventilation%20fan%20unit%20on%20a%20clean%20white%20background%2C%20extra%20large%20integrated%20solar%20panel%20on%20top%2C%20industrial%20grade%20housing%20with%20reinforced%20construction%20professional%20studio%20lighting%20with%20soft%20shadows%2C%20commercial%20product%20photography%20with%20sharp%20detail&width=400&height=400&seq=fan-commercial-003&orientation=squarish",
    cfm: "2,500 CFM",
    coverage: "Up to 3,000 sq ft",
    panel: "40W Solar Panel",
    price: "$1,095",
    badge: "",
  },
];

export default function SolarFansSection() {
  return (
    <section id="solar-fans" className="py-24 bg-brand-cream">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="flex items-center gap-3 justify-center mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
              <i className="ri-windy-line text-teal-600 text-xl"></i>
            </div>
            <span className="text-sm font-bold text-teal-600 tracking-wider uppercase">
              Solar Attic Fans
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight max-w-3xl mx-auto">
            Remove Trapped Heat With Solar-Powered Ventilation
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Solar attic fans actively pull superheated air out of your attic using free
            solar energy. They reduce attic temperatures by up to 40 degrees,
            extending your roof&apos;s lifespan and lowering cooling costs — all with
            zero operating expense.
          </p>
        </div>

        {/* How it works */}
        <div className="flex flex-col lg:flex-row gap-12 items-center mb-20">
          <div className="flex-1 w-full">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20cutaway%20diagram%20style%20photograph%20showing%20a%20solar%20attic%20fan%20installed%20on%20a%20residential%20roof%20with%20visible%20airflow%20arrows%20showing%20hot%20air%20being%20extracted%20from%20the%20attic%20space%2C%20cross-section%20view%20of%20the%20home%20showing%20the%20attic%20area%2C%20bright%20educational%20illustration%20style%20with%20warm%20colors%2C%20clean%20professional%20infographic%20photography&width=700&height=450&seq=fan-howworks-diagram-001&orientation=landscape"
                alt="How solar attic fans work"
                className="w-full h-[400px] object-cover object-top"
              />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              How Solar Attic Fans Work
            </h3>
            <div className="space-y-6">
              {[
                {
                  step: "01",
                  title: "Solar Panel Absorbs Sunlight",
                  desc: "The integrated solar panel converts sunlight into electricity to power the fan motor — no wiring or electrical costs needed.",
                },
                {
                  step: "02",
                  title: "Fan Extracts Hot Air",
                  desc: "The high-CFM motor pulls superheated air (up to 160 degrees) out of your attic through the roof-mounted vent.",
                },
                {
                  step: "03",
                  title: "Cool Air Replaces Hot Air",
                  desc: "As hot air exits, cooler outside air is drawn in through soffit vents, creating continuous natural ventilation.",
                },
                {
                  step: "04",
                  title: "Your Home Stays Cooler",
                  desc: "With attic temperatures reduced by up to 40 degrees, your AC works less, your roof lasts longer, and your bills drop.",
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl flex-shrink-0">
                    <span className="text-teal-600 font-bold text-sm">{item.step}</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {fanModels.map((fan) => (
            <div
              key={fan.name}
              className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group relative"
            >
              {fan.badge && (
                <div className="absolute top-4 right-4 z-10 bg-brand-red text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  {fan.badge}
                </div>
              )}
              <div className="bg-gray-50 p-8">
                <img
                  src={fan.image}
                  alt={fan.name}
                  className="w-full h-[250px] object-contain group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">{fan.name}</h4>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Airflow</span>
                    <span className="font-semibold text-gray-900">{fan.cfm}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Coverage</span>
                    <span className="font-semibold text-gray-900">{fan.coverage}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Solar Panel</span>
                    <span className="font-semibold text-gray-900">{fan.panel}</span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Starting at</p>
                    <p className="text-2xl font-bold text-brand-navy">{fan.price}</p>
                  </div>
                  <a
                    href="#consultation"
                    className="bg-brand-navy text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-brand-navy-dark transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Get Quote
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
